<template>
  <v-dialog
    v-model="dialog"
    :max-width="maxWidth"
  >
    <template #activator="{ on: dialogOn, attrs: dialogAttrs }">
      <ActivatorButton
        v-if="buttonTitle"
        :parent-attrs="dialogAttrs"
        :parent-on="dialogOn"
        :icon-or-title="buttonTitle"
        :tooltip-text="title"
        :disabled="disabled"
        :button-style="{ display: visible ? 'flex' : 'none' }"
        @activate="onButtonActivate"
      />
    </template>
    <v-form ref="form">
      <v-card>
        <v-toolbar
          dark
          dense
          color="primary"
        >
          <v-toolbar-title>{{ title }}</v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            @click="closeDialog()"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-title />

        <v-card-text>
          <v-container>
            <v-row
              v-if="warning"
              style="color: red; font-size: larger"
            >
              {{ warning }}
            </v-row>
            <v-row>
              <v-col
                v-if="showOperations"
                cols="4"
              >
                <v-select
                  v-model="operationType"
                  :items="['SMIRKS', 'SMILES', 'SMARTS']"
                  label="Operation"
                />
              </v-col>
              <v-col
                v-if="showOperations"
                cols="4"
              >
                <v-select
                  v-model="severity"
                  :items="['INFO', 'WARNING', 'ERROR']"
                  label="Severity"
                />
              </v-col>
              <v-col
                v-if="showOperations"
                cols="4"
              >
                <v-select
                  v-model="action"
                  :items="['CONTINUE', 'SKIP', 'STOP']"
                  label="Action"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-file-input
                  v-model="files"
                  :label="label"
                  show-size
                  :multiple="multiple"
                  :rules="[(v) => (v && !!v.length) || 'Required']"
                />
              </v-col>
            </v-row>
            <slot
              name="filesInfo"
              v-bind="{ filesHeaders, replace }"
            >
              <template v-for="fileHeaders in filesHeaders">
                <v-row :key="fileHeaders.fileName">
                  <v-col v-if="showId || showActivity || hasTableFile">
                    {{ fileHeaders.fileName }}
                  </v-col>
                  <v-col v-if="showId">
                    <ClearableSelect
                      v-model="fileHeaders.idField"
                      :items="fileHeaders.headers"
                      label="ID field"
                    />
                  </v-col>
                  <v-col
                    v-if="
                      (showId || showActivity || hasTableFile) &&
                      (fileHeaders.fileName.toLowerCase().endsWith('.csv') ||
                        fileHeaders.fileName.toLowerCase().endsWith('.xlsx'))
                    "
                  >
                    <ClearableSelect
                      v-model="fileHeaders.strField"
                      :items="fileHeaders.headers"
                      label="Structure field"
                    />
                  </v-col>
                  <v-col
                    v-if="
                      showActivity &&
                      (fileHeaders.fileName.toLowerCase().endsWith('.csv') ||
                        fileHeaders.fileName.toLowerCase().endsWith('.xlsx'))
                    "
                  >
                    <ClearableSelect
                      v-model="fileHeaders.actField"
                      :items="fileHeaders.headers"
                      label="Activity field"
                    />
                  </v-col>
                  <v-col v-if="showFillAdditionalProps">
                    <v-checkbox
                      class="fill-additional-props"
                      v-model="fileHeaders.fillAdditionalProps"
                      label="Fill additional properties"
                    />
                  </v-col>
                </v-row>
              </template>
            </slot>
            <v-row v-if="showReplace">
              <v-spacer />
              <v-checkbox
                v-model="replace"
                label="Replace"
                hint="Replace objects if their name match"
              />
            </v-row>
            <v-row
              v-if="message"
              style="color: red; font-size: larger"
            >
              {{ message }}
            </v-row>
          </v-container>
          <v-progress-linear
            v-if="uploading"
            indeterminate
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            :disabled="uploading"
            @click="closeDialog()"
            aria-label="Cancel"
          >
            Cancel
          </v-btn>
          <v-btn
            :disabled="uploading || !files.length"
            @click="onUpload"
            aria-label="Upload"
          >
            Upload
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import DialogBase from '@/components/dialogs/DialogBase.vue'
import { getErrorMessage } from '@/main'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import ActivatorButton from '@/components/ActivatorButton.vue'
import ClearableSelect from '@/components/ui/ClearableSelect.vue'

export default {
  components: { ActivatorButton, ClearableSelect },
  mixins: [DialogBase],

  props: {
    preflightUrl: {
      type: String,
      default: null,
    },
    uploadUrl: {
      type: String,
      required: true,
    },
    showId: {
      type: Boolean,
      default: false,
    },
    showActivity: {
      type: Boolean,
      default: false,
    },
    showStdizer: {
      type: Boolean,
      default: false,
    },
    showOperations: {
      type: Boolean,
      default: false,
    },
    showReplace: {
      type: Boolean,
      default: false,
    },
    showFillAdditionalProps: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: 'TXT, SDF, MOL, SMI, CSV or XLSX file(s)',
    },
    warning: {
      type: String,
      default: null,
    },
    options: {
      type: Object,
      default: null,
    },
  },

  data: () => ({
    comment: null,
    uploading: false,
    filesHeaders: [],
    localFiles: [],
    operationType: 'SMILES',
    severity: null,
    action: null,
    replace: false,
    defaultVTooltipProps,
  }),

  computed: {
    files: {
      get() {
        return Array.isArray(this.localFiles) ? this.localFiles : [this.localFiles]
      },
      set(val) {
        !val ? (this.localFiles = []) : Array.isArray(val) ? (this.localFiles = val) : (this.localFiles = [val])
      },
    },
    hasFileWithFields() {
      return this.files.find(
        (f) =>
          f.name.toLowerCase().endsWith('.json') ||
          f.name.toLowerCase().endsWith('.csv') ||
          f.name.toLowerCase().endsWith('.xlsx') ||
          f.name.toLowerCase().endsWith('.sdf') ||
          f.name.toLowerCase().endsWith('.rdf'),
      )
    },
    hasTableFile() {
      return this.files.find(
        (f) => f && (f.name.toLowerCase().endsWith('.csv') || f.name.toLowerCase().endsWith('.xlsx')),
      )
    },
  },

  watch: {
    files(val) {
      this.filesHeaders = []
      this.message = null

      if (this.preflightUrl && val.length) {
        this.message = null
        this.uploading = true

        const formData = new FormData()
        this.files.forEach((f) => formData.append('files[]', f, f.name))

        this.$axios
          .post(this.preflightUrl, formData)
          .then((response) => {
            this.uploading = false
            this.filesHeaders = response.data
            if (this.showFillAdditionalProps) {
              this.filesHeaders.forEach((f) => (f.fillAdditionalProps = true))
            }
          })
          .catch((error) => {
            this.uploading = false
            this.message = getErrorMessage(error)
          })
      }
    },
  },

  methods: {
    closeDialog() {
      this.dialog = false
    },

    onUpload() {
      if (this.files && this.files.length && this.$refs.form.validate()) {
        this.uploading = true

        const formData = new FormData()
        for (let file of this.files) formData.append('files[]', file, file.name)

        formData.append(
          'request',
          JSON.stringify({
            filesInfo: this.filesHeaders.map((h) => ({
              fileName: h.fileName,
              idField: h.idField,
              strField: h.strField,
              actField: h.actField,
              fillAdditionalProps: h.fillAdditionalProps,
            })),
            operationType: this.operationType,
            severity: this.severity,
            action: this.action,
            replace: this.replace,
            options: this.options,
          }),
        )

        this.$axios
          .post(this.uploadUrl, formData)
          .then((response) => {
            this.uploading = false
            this.closeDialog()
            this.$emit('uploaded', response.data)
          })
          .catch((error) => {
            this.uploading = false
            this.message = getErrorMessage(error)
          })
      }
    },
  },
}
</script>

<style scoped>
.fill-additional-props {
  padding: 0;
  margin: 0;
}
</style>
